import React, { useMemo } from "react";
import { usePreferences } from "../../hooks/usePreferences";
import { useSettings } from "../../contexts/SettingsContext";
import { useAuth } from "../../hooks/useAuth";
import debugx from "debugx";
import { DebugTab } from "./DebugTab";
import { GeneralTab } from "./GeneralTab";
import { MapTab } from "./MapTab";
import { ActivityTab } from "./ActivityTab";
import { HelpTab } from "./HelpTab";

const debug = debugx("bikedash:settings");

interface SettingsModalProps {
  // Props are no longer needed - using global context
}

// Internal components now imported from separate files - removed ~900 lines
// Previously defined here: CacheManagementSection, SuppressedPatternsList, ToggleSwitch, SettingRow

export function SettingsModal({}: SettingsModalProps) {
  const {
    userPreferences,
    mapPreferences,
    updateUserPreferences,
    updateMapPreferences,
    toggleStarredMap,
    setApiKey,
  } = usePreferences();

  const { isOpen, currentTab, closeSettings, setCurrentTab } = useSettings();
  const { user } = useAuth();

  // Check if debug tab should be shown
  const showDebugTab = useMemo(() => {
    // Check for ?debug query param
    const urlParams = new URLSearchParams(window.location.search);
    const hasDebugParam = urlParams.has("debug");

    // Show if user is admin OR ?debug param is present
    return user?.isAdmin || hasDebugParam;
  }, [user]);

  debug("SettingsModal render - userPreferences:", userPreferences);
  debug(
    "SettingsModal render - keepScreenOn specifically:",
    userPreferences?.keepScreenOn,
  );
  debug("SettingsModal render - mapPreferences:", mapPreferences);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={closeSettings}>
      <div
        className="modal settings-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="close-btn" onClick={closeSettings}>
          <span>×</span>
        </button>

        <div className="settings-tabs">
          <button
            className={`tab ${currentTab === "general" ? "active" : ""}`}
            onClick={() => setCurrentTab("general")}
          >
            Settings
          </button>
          <button
            className={`tab ${currentTab === "map" ? "active" : ""}`}
            onClick={() => setCurrentTab("map")}
          >
            Map
          </button>
          <button
            className={`tab ${currentTab === "activity" ? "active" : ""}`}
            onClick={() => setCurrentTab("activity")}
          >
            Activity
          </button>
          <button
            className={`tab ${currentTab === "help" ? "active" : ""}`}
            onClick={() => setCurrentTab("help")}
          >
            Help
          </button>
          {showDebugTab && (
            <button
              className={`tab ${currentTab === "debug" ? "active" : ""}`}
              onClick={() => setCurrentTab("debug")}
            >
              Debug
            </button>
          )}
        </div>

        <div className="settings-content">
          {/* General Tab */}
          {currentTab === "general" && (
            <GeneralTab
              userPreferences={userPreferences}
              updateUserPreferences={updateUserPreferences}
              setApiKey={setApiKey}
            />
          )}

          {/* Map Tab */}
          {currentTab === "map" && (
            <MapTab
              userPreferences={userPreferences}
              mapPreferences={mapPreferences}
              updateUserPreferences={updateUserPreferences}
              updateMapPreferences={updateMapPreferences}
              toggleStarredMap={toggleStarredMap}
            />
          )}
          {/* Activity Tab */}
          {currentTab === "activity" && (
            <ActivityTab
              userPreferences={userPreferences}
              updateUserPreferences={updateUserPreferences}
            />
          )}

          {/* Help Tab */}
          {currentTab === "help" && <HelpTab />}

          {/* Debug Tab */}
          {showDebugTab && currentTab === "debug" && <DebugTab />}
        </div>
      </div>
    </div>
  );
}
